import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MusicContext = createContext();

export const useMusic = () => {
  const ctx = useContext(MusicContext);
  if (!ctx) throw new Error('useMusic must be used inside MusicProvider');
  return ctx;
};

export const MusicProvider = ({ children }) => {
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(() => sessionStorage.getItem('owtw_muted') === 'true');
  const [volume, setVolume] = useState(() => {
    const v = parseFloat(sessionStorage.getItem('owtw_volume'));
    return isNaN(v) ? 0.6 : v;
  });

  const audioRef = useRef(null);
  const hasAutoPlayedRef = useRef(false);

  // Fetch playlist once
  useEffect(() => {
    axios.get(`${API}/playlist`).then(res => setPlaylist(res.data || [])).catch(() => {});
  }, []);

  // Initialize audio element once
  useEffect(() => {
    if (!audioRef.current) {
      const el = new Audio();
      el.preload = 'metadata';
      el.volume = volume;
      audioRef.current = el;
    }
    const el = audioRef.current;
    const handleEnded = () => {
      setCurrentIndex(prev => {
        const total = playlist.length || 1;
        return (prev + 1) % total;
      });
    };
    el.addEventListener('ended', handleEnded);
    return () => el.removeEventListener('ended', handleEnded);
    // eslint-disable-next-line
  }, [playlist]);

  // Sync src and play/pause with state
  useEffect(() => {
    if (!audioRef.current || playlist.length === 0) return;
    const track = playlist[currentIndex];
    if (!track) return;
    if (audioRef.current.src !== track.audio_url) {
      audioRef.current.src = track.audio_url;
    }
    if (isPlaying) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [currentIndex, isPlaying, playlist]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = isMuted;
    sessionStorage.setItem('owtw_muted', String(isMuted));
  }, [isMuted]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    sessionStorage.setItem('owtw_volume', String(volume));
  }, [volume]);

  // Auto-play 3s after load with fade-in
  useEffect(() => {
    if (playlist.length === 0 || hasAutoPlayedRef.current) return;
    const userPrevMuted = sessionStorage.getItem('owtw_muted') === 'true';
    let startTimer = null;
    let toastTimer = null;

    const showMuteToast = () => {
      if (userPrevMuted) return;
      toast('🎵 Now Playing Official Playlist', {
        description: 'Tap the speaker icon in the header to mute',
        duration: 6000,
        action: { label: 'Mute', onClick: () => setIsMuted(true) }
      });
    };

    const fadeInVolume = (targetVolume, durationMs = 1500) => {
      if (!audioRef.current) return;
      const el = audioRef.current;
      el.volume = 0;
      const steps = 30;
      const stepTime = durationMs / steps;
      let i = 0;
      const iv = setInterval(() => {
        i += 1;
        if (!audioRef.current) { clearInterval(iv); return; }
        audioRef.current.volume = Math.min(targetVolume, (i / steps) * targetVolume);
        if (i >= steps) clearInterval(iv);
      }, stepTime);
    };

    const attemptPlay = async () => {
      if (!audioRef.current) return;
      const targetVol = volume || 0.6;
      try {
        audioRef.current.muted = userPrevMuted;
        await audioRef.current.play();
        hasAutoPlayedRef.current = true;
        setIsPlaying(true);
        setIsMuted(userPrevMuted);
        if (!userPrevMuted) fadeInVolume(targetVol, 1500);
        toastTimer = setTimeout(showMuteToast, 3000);
        return;
      } catch { /* fall through */ }
      try {
        audioRef.current.muted = true;
        await audioRef.current.play();
        hasAutoPlayedRef.current = true;
        setIsPlaying(true);
      } catch { /* wait for interaction */ }
    };
    startTimer = setTimeout(attemptPlay, 3000);

    const tryStart = () => {
      if (!audioRef.current) return;
      const targetVol = volume || 0.6;
      const wasMuted = audioRef.current.muted;
      if (!userPrevMuted) {
        audioRef.current.muted = false;
        setIsMuted(false);
        if (wasMuted) fadeInVolume(targetVol, 1500);
      }
      if (audioRef.current.paused) {
        audioRef.current.play().then(() => {
          if (!userPrevMuted) fadeInVolume(targetVol, 1500);
        }).catch(() => {});
      }
      if (!hasAutoPlayedRef.current) {
        hasAutoPlayedRef.current = true;
        setIsPlaying(true);
      }
      if (toastTimer) clearTimeout(toastTimer);
      toastTimer = setTimeout(showMuteToast, 3000);
    };

    const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(ev => window.addEventListener(ev, tryStart, { once: true, passive: true }));
    return () => {
      if (startTimer) clearTimeout(startTimer);
      if (toastTimer) clearTimeout(toastTimer);
      events.forEach(ev => window.removeEventListener(ev, tryStart));
    };
    // eslint-disable-next-line
  }, [playlist.length]);

  const playPause = useCallback(() => {
    if (playlist.length === 0) return;
    setIsPlaying(p => !p);
  }, [playlist.length]);

  const next = useCallback(() => {
    if (playlist.length === 0) return;
    setCurrentIndex(i => (i + 1) % playlist.length);
  }, [playlist.length]);

  const prev = useCallback(() => {
    if (playlist.length === 0) return;
    setCurrentIndex(i => (i - 1 + playlist.length) % playlist.length);
  }, [playlist.length]);

  const selectTrack = useCallback((idx) => {
    setCurrentIndex(idx);
    setIsPlaying(true);
  }, []);

  const value = {
    playlist,
    currentIndex,
    isPlaying,
    isMuted,
    volume,
    setIsMuted,
    setVolume,
    playPause,
    next,
    prev,
    selectTrack,
    currentTrack: playlist[currentIndex] || null
  };

  return <MusicContext.Provider value={value}>{children}</MusicContext.Provider>;
};
